import {useCallback, useRef} from 'react';
import {nanoid} from 'nanoid';

import {
  deleteCurrentStackingContextTarget,
  getCurrentStackingContextDepth,
  getStackingContextDepthForElement,
  isCurrentStackingContextTarget,
  setCurrentStackingContextTarget,
  STACKABLE_CONTEXT_ATTR,
  Visibility,
  VisibilityEvents,
} from './stacking-context';

export {STACKABLE_CONTEXT_ATTR};

/**
 * Depth of the currently open stacking contexts — the "is anything open?" question the tour,
 * onboarding-questionnaire, report-editor Escape and query-block context-panel gates ask.
 */
export function getStackingDepth(): number {
  return getCurrentStackingContextDepth();
}

/**
 * How deep in the stacking hierarchy an element sits, counted by walking its ancestors (through
 * shadow boundaries) for `STACKABLE_ELEMENTS` matches. Compare against `getStackingDepth()` to ask
 * "is something open above me?" rather than "is anything open at all?".
 */
export function getStackingDepthForElement(element: Element | null): number {
  return getStackingContextDepthForElement(element);
}

export type StackableEvent = CustomEvent<{target: string; stackSize: number}>;

interface UseStackableOptions {
  /** When true, this component neither joins the stacking context nor dispatches `stackable-*` events. */
  ignoreStacking?: boolean;
}

interface StackableApi {
  /** Stable identity for this instance, used as its entry in the shared `contextStack`. */
  stackId: string;
  /** Dispatch `stackable-open` from the host, then push this instance onto the shared stack. */
  notifyOpen: (host: HTMLElement | null) => StackableEvent | null;
  /** Dispatch `stackable-closed` from the host, then pop this instance off the shared stack. */
  notifyClosed: (host: HTMLElement | null) => StackableEvent | null;
  /** Leave the shared stack without dispatching — for unmounting while already closed. */
  release: () => void;
  /** Whether this instance is the innermost open stackable, i.e. the one Escape and outside-clicks belong to. */
  isCurrentTarget: () => boolean;
}

/**
 * React entry point into the stacking-context protocol in `./stacking-context`: mints a stable
 * `stackId`, registers it in the shared `contextStack`, and dispatches `stackable-open` /
 * `stackable-closed` (bubbles + composed) from the host element with
 * `detail: {target: stackId, stackSize}`.
 *
 * Sharing the one `Set` rather than mirroring it in a counter is load-bearing for two reasons: the
 * global "is anything open?" gates (tours, the onboarding questionnaire, the report-editor Escape
 * handler, the query-block context panel) read `getCurrentStackingContextDepth()`, and
 * `isCurrentStackingContextTarget()` resolves "innermost" from the Set's *insertion order*, which no
 * separate count can reconstruct.
 *
 * Membership changes land after the event dispatch, so `stackSize` and any listener reading the depth
 * see the depth without this instance on open, and still including it on close.
 *
 * Because that ordering is the consumer's to preserve, leaving the stack is explicit rather than an
 * effect cleanup here: an unmount while open must go through `notifyClosed` (which dispatches first,
 * then removes), and an unmount while closed through `release`. A consumer that does neither strands
 * its id in the shared `Set` and suppresses every depth gate for the rest of the page's life.
 */
export function useStackable({ignoreStacking = false}: UseStackableOptions = {}): StackableApi {
  const stackIdRef = useRef<string>(``);
  if (!stackIdRef.current) {
    stackIdRef.current = nanoid();
  }
  // Whether this instance currently holds an entry in the shared stack, so open/close/unmount stay balanced.
  const registeredRef = useRef(false);

  const dispatch = useCallback(
    (host: HTMLElement | null, visibility: Visibility): StackableEvent | null => {
      if (!host || ignoreStacking) {
        return null;
      }
      const event = new CustomEvent(VisibilityEvents[visibility], {
        bubbles: true,
        composed: true,
        detail: {target: stackIdRef.current, stackSize: getCurrentStackingContextDepth()},
      }) as StackableEvent;
      host.dispatchEvent(event);
      return event;
    },
    [ignoreStacking],
  );

  const release = useCallback(() => {
    if (registeredRef.current) {
      deleteCurrentStackingContextTarget(stackIdRef.current);
      registeredRef.current = false;
    }
  }, []);

  const notifyOpen = useCallback(
    (host: HTMLElement | null) => {
      const event = dispatch(host, Visibility.Open);
      // Membership is gated on `ignoreStacking` alone, never on whether an event went out. A host
      // that is momentarily null costs the sibling-close handshake, which degrades to "peers stay
      // open"; gating membership on it instead would drop an open component out of the depth gates,
      // which is the silent tour-over-modal failure this hook exists to prevent. `release` is
      // likewise host-independent, so the two stay balanced whatever the host is.
      if (!ignoreStacking && !registeredRef.current) {
        setCurrentStackingContextTarget(stackIdRef.current);
        registeredRef.current = true;
      }
      return event;
    },
    [dispatch, ignoreStacking],
  );

  const notifyClosed = useCallback(
    (host: HTMLElement | null) => {
      const event = dispatch(host, Visibility.Closed);
      release();
      return event;
    },
    [dispatch, release],
  );

  const isCurrentTarget = useCallback(
    () => registeredRef.current && isCurrentStackingContextTarget(stackIdRef.current),
    [],
  );

  return {stackId: stackIdRef.current, notifyOpen, notifyClosed, release, isCurrentTarget};
}
